import '../css/ingredientsStory.css';
import type { Bottle3D } from './components/bottle3d';
import { firstInteraction, nearViewport } from './components/firstInteraction';
import {
  easeInOut,
  lerp,
  mountScrollScenes,
  span,
  TAU,
  type SceneRender,
} from './components/scrollScenes';
import { onReady } from './utils/ready';

/** Model units of room above the bottle for the lifted lid; keep in sync with --ing-headroom in the CSS. */
const HEADROOM = 1.6;

// Scroll timeline (0..1 through the pinned scene).
// Most of the scroll is the orbit; the open, pour and seal finale is packed into a short stretch at the end.
const INTRO = [0, 0.07] as const; // chips pop out around the bottle
const ORBIT = [0.04, 0.72] as const; // chips circle the bottle, front one is detailed
const OPEN = [0.68, 0.76] as const; // lid lifts and swings aside
const POUR = [0.74, 0.88] as const; // chips arc up and drop into the neck, one after another
const SEAL = [0.89, 0.96] as const; // lid comes back and screws down
const POUR_EACH = 0.05; // scroll each chip takes to drop in
/** Once the lid is slightly off, the rest plays by itself over this many seconds. */
const FINALE_TRIGGER = OPEN[0] + 0.015;
const FINALE_SECONDS = 3.4;
const FINALE_REWIND_SECONDS = 1.2;
/** ...after holding this long at the trigger, so the last ingredient's card can be read before the pour. */
const FINALE_HOLD_SECONDS = 1.5;
/** A picked chip's card stays (and the idle auto-scroll waits) this long... */
const PICK_HOLD_MS = 4000;
/** ...or until the story has scrolled on this far. */
const PICK_RELEASE = 0.02;
/** If the visitor pauses mid-story for this long, the page scrolls on by itself... */
const IDLE_MS = 1500;
/** ...at this much story progress per second (the orbit takes ~13s), until the finale takes over. */
const IDLE_SPEED = 0.05;
/** Mid-story, after this long without input a "Keep scrolling" pill slides up, in case they think the page is stuck. */
const NUDGE_MS = 1000;
/** Input that means the visitor has taken back control (our own auto-scroll doesn't fire these). */
const USER_INPUT = [
  'wheel',
  'touchstart',
  'touchmove',
  'keydown',
  'pointerdown',
] as const;

let bottle: Bottle3D | undefined;
/** Whether that is the tub or the pouch (the hand-off lines the tub packshot up with the render). */
let bottleKind: 'bottle' | 'pouch' = 'bottle';
/** True while the gallery photo is flying into the stage; the 3D bottle holds still until it lands. */
let handingOff = false;
/** The idle auto-scroll waits until then (performance.now()), e.g. while a picked chip's card is read. */
let idleHeldUntil = 0;

/**
 * Idle auto-play: while the story is pinned and the visitor pauses, gently scroll the page on so the
 * orbit keeps going; once the lid trigger is reached the finale autoplay finishes it. Any input stops it.
 * Mid-story, a pause also brings up a "Keep scrolling" pill (the hint), which stays while the page moves on by
 * itself and goes as soon as they scroll, tap or press a key.
 */
function autoAdvanceWhenIdle(track: HTMLElement, stage: HTMLElement) {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let lastInput = performance.now();
  const onInput = () => (lastInput = performance.now());
  USER_INPUT.forEach((type) =>
    window.addEventListener(type, onInput, { passive: true }),
  );

  const hint = stage.querySelector<HTMLElement>('[data-ing-hint]');
  let nudging = false;
  const nudge = (on: boolean) => {
    if (on === nudging) return;
    nudging = on;
    hint?.classList.toggle('is-nudging', on);
  };

  let visible = false;
  let frame = 0;
  let last = 0;
  let carry = 0; // sub-pixel remainder, so slow speeds still move

  const tick = (now: number) => {
    frame = visible ? requestAnimationFrame(tick) : 0;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
    last = now;

    const travel = track.offsetHeight - stage.offsetHeight;
    const p =
      (stage.getBoundingClientRect().top - track.getBoundingClientRect().top) /
      travel;
    nudge(
      travel > 0 &&
        now - lastInput > NUDGE_MS &&
        p > INTRO[1] &&
        p < FINALE_TRIGGER,
    );

    if (now - lastInput < IDLE_MS || now < idleHeldUntil || document.hidden)
      return;
    if (travel <= 0 || p <= 0.02 || p >= FINALE_TRIGGER) return;

    carry += IDLE_SPEED * travel * dt;
    const step = Math.floor(carry);
    if (step > 0) {
      carry -= step;
      window.scrollBy({ top: step, behavior: 'instant' });
    }
  };

  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (!visible) nudge(false);
    if (visible && !frame) {
      last = 0;
      frame = requestAnimationFrame(tick);
    }
  }).observe(track);
}

// Hand-off, 0..1 from the story's top entering the viewport to the stage pinning:
/** The flat photo (ghost) lifts out of the gallery... */
const HANDOFF_DEPART = [0, 0.12] as const;
/** ...dissolves mid-air into the real 3D bottle flying the same path, which spins once to show it's 3D... */
const HANDOFF_MORPH = [0.3, 0.55] as const;
/** ...lands beside the headline at this share of its full size... */
const HANDOFF_LAND_SCALE = 0.6;
/** ...then grows to full size as the stage pins. */
const HANDOFF_GROW = [0.8, 1] as const;
/** Peak tilt of the flat photo (degrees) and lift of the arc (share of the bottle's height). */
const HANDOFF_TILT = -6;
const HANDOFF_LIFT = 0.08;
/**
 * Where the tub sits in its packshot (measured across the range, Oct 2026): it fills about this share of the photo's
 * height, centred this far down. The 3D tub draws bigger than the photo in the same box, so the flight aims the photo
 * at the render's real outline, and the two line up as they crossfade.
 */
const PACKSHOT_TUB = { fill: 0.68, centre: 0.42 };

type Box = { x: number; y: number; w: number; h: number }; // centre + size, viewport px
const boxOf = (r: DOMRect): Box => ({
  x: r.left + r.width / 2,
  y: r.top + r.height / 2,
  w: r.width,
  h: r.height,
});

/** A fixed, hidden copy of a product photo for flying between sections (styled by .ing-ghost). */
function createGhost() {
  const ghost = document.createElement('div');
  ghost.className = 'ing-ghost';
  ghost.setAttribute('aria-hidden', 'true');
  const img = document.createElement('img');
  img.alt = '';
  img.decoding = 'async';
  ghost.append(img);
  document.body.append(ghost);
  return { ghost, img };
}

// Docking legs (dockIntoRoutines, then dockIntoWhy), each 0..1 over its own stretch of scroll.
/** The sealed bottle (as a flat photo ghost) leaves the stage... */
const DOCK_DEPART = [0, 0.1] as const;
/** ...and hands over to the small bottle beside the heading as it lands. */
const DOCK_LAND = [0.9, 1] as const;
/**
 * The screen height the bottle flights measure against: the small viewport, which stays put while a phone's address
 * bar slides in and out. innerHeight changes with it, which made the legs start and stop mid-scroll (the bottle
 * flashing between sections).
 */
const viewHeight = () => document.documentElement.clientHeight;
const viewWidth = () => document.documentElement.clientWidth;
/** Where the routines bottle sits (share of the viewport height) when the flight ends. */
const DOCK_AT = 0.45;
/** The bottom range shelf lands lower down the screen, so its gap opens and jostles as it comes into view. */
const SHELF_DOCK_AT = 0.8;
/** ...or, when there are related products, as their heading reaches this share of the screen (the middle). */
const SHELF_LAND_LINE = 0.5;
/** ...but not until the gap's bottom edge is up to this share of the screen (fully in view). */
const SHELF_IN_VIEW = 0.95;
/** Gap (px) the bottle keeps above the bottom edge while it waits for a target still coming up (holdInView). */
const HOLD_IN_VIEW_GAP = 16;
/** The bottom shelf's gap opens once the bottle's flight into it is this far along... */
const SHELF_OPEN_AT = 0.55;
/** ...and its neighbours may jostle again once the flight has been wound back below this. */
const SHELF_JOSTLE_RESET = 0.9;
/** Scroll (share of the viewport height) the bottle rests in a queued stop before flying on to the next. */
const QUEUE_DWELL = 0.2;
// Narrow layout, between routines and Why OneVit: the routines section is taller than the screen, so as its bottle
// reaches the site header it tucks into the header beside the logo and waits there until Why OneVit comes up.
/** Gap (px) under the header at which the bottle starts tucking in. */
const PARK_GAP = 8;
/** Scroll (px) over which it moves from the heading into the header. */
const TUCK_TRAVEL = 120;
/** Its size (px) in the header, white tile included (.ing-ghost --tuck), and its gap (px) from the logo. */
const TUCK_SIZE = 40;
const TUCK_SPACING = 12;
// Wide layout, FAQ to the range shelf: that stretch is long too, so instead of the header the bottle rides along in
// the right-hand gutter beside the page (as the source reaches the header it moves out there, TUCK_TRAVEL) and
// drops into the shelf's gap from there.
/** Its size (px) while riding. */
const RIDE_SIZE = 64;
/** The gutter must be at least this wide (px) to hold it; a narrower one tucks into the header like mobile. */
const RIDE_MIN_GUTTER = 90;
/** The gentle bob (px) and tilt (deg) as the page scrolls past, and how many px of scroll make one swing of each. */
const RIDE_BOB = 6;
const RIDE_BOB_PERIOD = 90;
const RIDE_TILT = 8;
const RIDE_TILT_PERIOD = 140;

/** The gallery photo in view: the carousel loops, so the visible slide nearest its centre. */
function visibleGalleryPhoto(gallery: HTMLElement) {
  const box = gallery.getBoundingClientRect();
  const mid = box.left + box.width / 2;
  let best: HTMLImageElement | undefined;
  let bestDist = Infinity;
  // <carousel-slide> renders as div[data-carousel-slide] (CarouselSlideTagHelper).
  for (const img of gallery.querySelectorAll<HTMLImageElement>(
    '[data-carousel-slide]:not(.hidden) picture img', // not the pharmacist badge
  )) {
    const r = img.getBoundingClientRect();
    const dist = Math.abs(r.left + r.width / 2 - mid);
    if (r.width && dist < bestDist) {
      bestDist = dist;
      best = img;
    }
  }
  return best;
}

type HeaderLeg = {
  /** The photo it leaves from; looked up again after a resize (the page has mobile and desktop copies). */
  source: () => HTMLImageElement | undefined;
  /** The picture to fly, if not the source's own (the gallery may be showing a photo that isn't the bottle). */
  photo?: () => string | undefined;
  /** Where it lands: this box is the end of the flight... */
  target: HTMLElement;
  /** ...and this fades in as it arrives (defaults to the target). */
  reveal?: HTMLElement;
  /** 0..1 through the flight on: 0 before it starts, 1 once it has landed. */
  progress: () => number;
  /** While this matches, a source that would scroll off the top tucks into the header (TUCK_*) and waits. */
  tuckWhen: { readonly matches: boolean };
  /**
   * Where it waits (centre and size, in the viewport) while tuckWhen does not match: once the source reaches the site
   * header it moves there over TUCK_TRAVEL of scroll and rides (bobbing) until it flies on. Without it, a leg that
   * isn't tucking just flies from its source.
   */
  ride?: () => { x: number; y: number; h: number };
  /** Keep the reveal hidden until the bottle has landed (it may be on screen well before the flight starts). */
  hideUntilLanded?: boolean;
  /** Keep the source hidden once the bottle has landed (otherwise it shows again behind the bottle), until scrolled back. */
  keepSourceHidden?: boolean;
  /**
   * Aim at the target held inside the screen, so a target still rising from below the fold doesn't pull the bottle
   * off the bottom: it flies to just above the bottom edge and meets the target there as it comes up.
   */
  holdInView?: boolean;
  onFlying?: (flying: boolean) => void;
};

/**
 * One leg of the bottle's trip down the page, as a flat photo ghost. On mobile the sections are taller than the
 * screen, so once the source reaches the site header the bottle tucks in beside the logo, on a white tile, and waits
 * there until the next section comes up; then it flies on and lands in the target. The photos are square, so the
 * ghost is sized by height to keep the tub the same size end to end.
 */
function flyViaHeader({
  source: findSource,
  photo,
  target,
  reveal = target,
  progress,
  tuckWhen,
  ride,
  hideUntilLanded,
  keepSourceHidden,
  holdInView,
  onFlying,
}: HeaderLeg) {
  const { ghost, img: ghostImg } = createGhost();
  ghost.style.zIndex = '35'; // over the site header (z-30), where it waits on mobile; under drawers and modals (z-40)
  const header = document.querySelector<HTMLElement>('[data-header]');
  const logo = header?.querySelector<HTMLElement>('a[href="/"] img');

  let source: HTMLImageElement | undefined;
  let flying = false;
  let frame = 0;
  let sourceHidden = false; // keepSourceHidden: the source has been left hidden since landing

  const setFlying = (value: boolean) => {
    if (value === flying) return;
    flying = value;
    onFlying?.(value);
  };

  const land = (arrived = true) => {
    ghost.style.opacity = '0';
    ghost.style.setProperty('--tuck', '0');
    sourceHidden = Boolean(keepSourceHidden && arrived);
    if (source) source.style.opacity = sourceHidden ? '0' : '';
    reveal.style.opacity = hideUntilLanded && !arrived ? '0' : '';
    source = undefined;
    setFlying(false);
  };

  const draw = () => {
    frame = 0;
    // Picked afresh until it leaves (the gallery may slide to another photo first), then held for the trip.
    if (!flying) source = findSource();
    const to = target.getBoundingClientRect();
    if (!source || !to.height) {
      if (flying) land();
      return;
    }
    const from = boxOf(source.getBoundingClientRect());
    const parkY =
      (header?.getBoundingClientRect().bottom ?? 0) + from.h / 2 + PARK_GAP;
    const tucking = tuckWhen.matches;
    const riding = !tucking && Boolean(ride); // wide layout with a rider spot
    const parked = (tucking || riding) && from.y < parkY;
    let tuck = 0;
    if (parked) {
      tuck = easeInOut(span(parkY - from.y, 0, TUCK_TRAVEL));
      let slot: { x: number; y: number; h: number };
      if (riding && ride) slot = ride();
      else {
        const l = logo?.getBoundingClientRect();
        slot = l
          ? {
              x: l.right + TUCK_SPACING + TUCK_SIZE / 2,
              y: l.top + l.height / 2,
              h: TUCK_SIZE,
            }
          : { x: from.x, y: parkY, h: TUCK_SIZE };
      }
      from.x = lerp(from.x, slot.x, tuck);
      from.y = lerp(parkY, slot.y, tuck);
      from.h = lerp(from.h, slot.h, tuck);
    }

    const a = progress();
    if (a >= 1 || (a <= 0 && !parked)) {
      if (flying) land(a >= 1);
      else {
        if (hideUntilLanded) reveal.style.opacity = a >= 1 ? '' : '0';
        // Landed without flying (loaded further down), or scrolled back in one jump.
        if (keepSourceHidden && sourceHidden !== a >= 1) {
          sourceHidden = a >= 1;
          source.style.opacity = sourceHidden ? '0' : '';
        }
      }
      return;
    }
    setFlying(true);

    const own = source.currentSrc || source.src;
    const src = photo?.() || own;
    if (ghostImg.getAttribute('src') !== src) ghostImg.src = src;

    const end = boxOf(to);
    if (holdInView)
      end.y = Math.min(end.y, viewHeight() - end.h / 2 - HOLD_IN_VIEW_GAP);
    const t = easeInOut(a);
    const arc = Math.sin(Math.PI * t);
    const size = lerp(from.h, end.h, t);
    const x = lerp(from.x, end.x, t);
    // A rider sways with the scroll while it waits, settling as it flies on (so there is no jump at take-off).
    const sway = riding && parked ? tuck * (1 - t) : 0;
    const y =
      lerp(from.y, end.y, t) -
      arc * size * HANDOFF_LIFT +
      sway * RIDE_BOB * Math.sin(window.scrollY / RIDE_BOB_PERIOD);
    const rotation =
      arc * HANDOFF_TILT +
      sway * RIDE_TILT * Math.sin(window.scrollY / RIDE_TILT_PERIOD);

    const depart = parked ? 1 : span(a, ...DOCK_DEPART); // a parked bottle has already left its spot
    const landed = span(a, ...DOCK_LAND);
    ghost.style.width = ghost.style.height = `${size.toFixed(1)}px`;
    ghost.style.transform = `translate(${(x - size / 2).toFixed(1)}px,${(y - size / 2).toFixed(1)}px) rotate(${rotation.toFixed(2)}deg)`;
    ghost.style.opacity = Math.min(depart, 1 - landed).toFixed(2);
    // The white tile only while it sits in the header: in at the very end of the tuck, out as soon as it flies on.
    const tile = riding ? 0 : span(tuck, 0.85, 1) * (1 - span(a, 0, 0.05)); // no tile out on the page
    ghost.style.setProperty('--tuck', tile.toFixed(2));
    // Another photo (not the bottle) stays put while the bottle flies out of its card.
    if (src === own) source.style.opacity = (1 - depart).toFixed(2);
    reveal.style.opacity = landed.toFixed(2);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(draw);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      // Phones resize as the address bar slides in and out mid-scroll, so only let go of the photo if it has gone
      // (the mobile/desktop copy swapped); dropping it mid-flight made the bottle flash between photo and nav.
      if (source && source.offsetParent === null) {
        source.style.opacity = '';
        source = undefined;
      }
      schedule();
    },
    { passive: true },
  );
  if (hideUntilLanded) reveal.style.opacity = '0'; // until the first draw says otherwise
  if (window.scrollY > 0) schedule(); // a visit from the top has nothing to draw yet (see scrollScenes)
}

/**
 * From the routines heading on into the "Why OneVit" heading ([data-why-dock]) as that section scrolls in, so every
 * section below the buy box is joined by the same bottle. Lands as the Why bottle reaches DOCK_AT.
 */
function dockIntoWhy() {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const target = document.querySelector<HTMLImageElement>('[data-why-dock]');
  const docks = Array.from(
    document.querySelectorAll<HTMLImageElement>('[data-ing-dock]'),
  );
  if (!target || !docks.length) return;
  const narrow = matchMedia('(max-width: 900px)');
  flyViaHeader({
    source: () => docks.find((d) => d.offsetParent !== null), // the routines copy displayed at this width
    target,
    progress: () =>
      span(
        viewHeight() - target.getBoundingClientRect().top,
        0,
        viewHeight() * (1 - DOCK_AT),
      ),
    tuckWhen: narrow,
  });
  // Next leg: on into the reviews section's score tile ([data-reviews-dock]) as it comes up.
  const reviews = document.querySelector<HTMLImageElement>(
    '[data-reviews-dock]',
  );
  if (reviews)
    flyViaHeader({
      source: () => target,
      target: reviews,
      progress: () =>
        span(
          viewHeight() - reviews.getBoundingClientRect().top,
          0,
          viewHeight() * (1 - DOCK_AT),
        ),
      tuckWhen: narrow,
    });
  // The last stops, each only if the page has it: the Instagram avatar, the FAQ tile, then the bottom shelf's gap. Each leg flies
  // on from the previous stop that exists. Unlike the legs above they start no earlier than the previous one lands: the
  // next stop can already be on screen while the bottle is still on its way (a tall screen, or Instagram's short section).
  let previous: HTMLImageElement = reviews ?? target;
  const instagram = document.querySelector<HTMLImageElement>(
    '[data-instagram-dock]',
  );
  const faq = document.querySelector<HTMLImageElement>('[data-faq-dock]');
  const shelfDock =
    document.querySelector<HTMLImageElement>('[data-shelf-dock]');
  const pageTop = (el: HTMLElement) =>
    el.getBoundingClientRect().top + window.scrollY;
  // A stop near the bottom of the page (the range shelf) may sit too low to ever reach its line, so it lands at
  // the furthest the page can scroll instead
  const maxScroll = () => document.documentElement.scrollHeight - viewHeight();
  const queuedProgress =
    (
      from: HTMLElement,
      to: HTMLElement,
      landAt = () => pageTop(to) - viewHeight() * DOCK_AT,
    ) =>
    () => {
      const land = Math.min(landAt(), maxScroll());
      // Starts as the stop comes on screen (or a normal flight's length before landing, if it lands sooner), and
      // rests a moment where it last landed first (otherwise it lifts straight back out, mid ring draw)
      const start = Math.max(
        Math.min(
          pageTop(to) - viewHeight(),
          land - viewHeight() * (1 - DOCK_AT),
        ),
        pageTop(from) - viewHeight() * DOCK_AT + viewHeight() * QUEUE_DWELL,
      );
      return span(window.scrollY, start, Math.max(land, start + 1));
    };
  // The shelf lands as the related products heading ("Complete your routine") reaches the middle of the screen, so
  // the gap and jostle play while the section is in view; with no related section, as the gap reaches SHELF_DOCK_AT.
  // Never before the whole gap is on screen, though (a tall related section, or stacked cards on mobile, can leave
  // the shelf below the fold at that point), or the landing would play out of sight.
  const relatedHeading = document.querySelector<HTMLElement>(
    '#related-products h2',
  );
  const shelfLandAt = (shelf: HTMLElement) => () => {
    const inView =
      pageTop(shelf) + shelf.offsetHeight - viewHeight() * SHELF_IN_VIEW;
    return relatedHeading
      ? Math.max(
          pageTop(relatedHeading) +
            relatedHeading.offsetHeight / 2 -
            viewHeight() * SHELF_LAND_LINE,
          inView,
        )
      : Math.max(pageTop(shelf) - viewHeight() * SHELF_DOCK_AT, inView);
  };

  if (instagram) {
    const avatar = instagram.closest<HTMLElement>('[data-instagram-avatar]');
    avatar?.classList.add('is-armed');
    const from = previous;
    const progress = queuedProgress(from, instagram);
    flyViaHeader({
      source: () => from,
      target: instagram,
      progress: () => {
        const a = progress();
        // The ring draws as the bottle settles in (the hand-over, DOCK_LAND) and stays for the rest of the way
        // down; scrolling back un-draws it.
        avatar?.classList.toggle('is-landed', a >= DOCK_LAND[0]);
        return a;
      },
      tuckWhen: narrow,
      hideUntilLanded: true,
      keepSourceHidden: true,
    });
    previous = instagram;
  }
  if (faq) {
    const from = previous;
    flyViaHeader({
      source: () => from,
      target: faq,
      progress: queuedProgress(from, faq),
      tuckWhen: narrow,
      hideUntilLanded: true,
      keepSourceHidden: true,
    });
    previous = faq;
  }
  if (shelfDock) {
    const shelf = shelfDock.closest<HTMLElement>('[data-shelf]');
    const from = previous;
    const header = document.querySelector<HTMLElement>('[data-header]');
    const landAt = shelfLandAt(shelfDock);
    const queued = queuedProgress(from, shelfDock, landAt);
    // Wide layout: the bottle leaves the source as it reaches the header, rides in the gutter, and only flies on
    // for a normal flight's length before it lands (never before it has finished moving out).
    const rideProgress = () => {
      const land = Math.min(landAt(), maxScroll());
      const departAt =
        pageTop(from) -
        (header?.getBoundingClientRect().bottom ?? 0) -
        PARK_GAP;
      const flyStart = Math.max(
        departAt + TUCK_TRAVEL,
        land - viewHeight() * (1 - DOCK_AT),
      );
      return span(window.scrollY, flyStart, Math.max(land, flyStart + 1));
    };
    // The rider spot: the middle of the gutter right of the page's content column (the FAQ's max-w-6xl box).
    const column = from.closest<HTMLElement>('.max-w-6xl');
    const gutterLeft = () =>
      column?.getBoundingClientRect().right ?? viewWidth();
    // Rides only where the gutter is wide enough to hold it; a narrow layout or a narrow gutter tucks into the
    // header instead, like mobile. Checked live (every frame, so on resize too), as a MediaQueryList's matches would be.
    const tuckInstead = {
      get matches() {
        return narrow.matches || viewWidth() - gutterLeft() < RIDE_MIN_GUTTER;
      },
    };
    const progress = () => (tuckInstead.matches ? queued() : rideProgress());
    const rideSpot = () => {
      const right = gutterLeft();
      return {
        x: right + (viewWidth() - right) / 2,
        y: viewHeight() * DOCK_AT,
        h: RIDE_SIZE,
      };
    };
    shelf?.classList.add('is-closed');
    let jostled = false;
    flyViaHeader({
      source: () => from,
      target: shelfDock,
      progress: () => {
        const a = progress();
        // The gap opens as the bottle approaches; on landing the neighbours jostle (once per arrival).
        shelf?.classList.toggle('is-closed', a <= SHELF_OPEN_AT);
        if (a >= 1 && !jostled) {
          jostled = true;
          shelf?.classList.remove('is-jostling');
          void shelf?.offsetWidth; // restart the animation
          shelf?.classList.add('is-jostling');
        } else if (a < SHELF_JOSTLE_RESET) {
          jostled = false;
          shelf?.classList.remove('is-jostling');
        }
        return a;
      },
      tuckWhen: tuckInstead,
      ride: rideSpot,
      hideUntilLanded: true,
      keepSourceHidden: true,
      holdInView: true,
    });
    previous = shelfDock;
  }
  // Mobile: once the last bottle scrolls away, it goes back into the nav and stays there for the rest of the page
  // (no next leg, so it never flies on); scrolling back up brings it back down into place.
  const last = previous;
  flyViaHeader({
    source: () => last,
    target: last,
    progress: () => 0,
    tuckWhen: narrow,
  });
}

/**
 * Narrow layout's version of handOffFromGallery: the gallery photo tucks into the header as the buy box scrolls by,
 * then drops into the story's bottle, landing as the stage pins.
 */
function galleryIntoStory(track: HTMLElement, slot: HTMLElement) {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const narrow = matchMedia('(max-width: 900px)'); // wider screens get handOffFromGallery; checked every frame
  const gallery = document.querySelector<HTMLElement>('[data-ing-handoff]');
  const photo = slot.querySelector<HTMLImageElement>('img'); // keeps its box under the 3D bottle
  const header = document.querySelector<HTMLElement>('[data-header]');
  if (!gallery || !photo) return;
  // Always the bottle (the variant's thumbnail), even if the gallery has been swiped to another photo.
  const bottlePhoto = () => {
    const img = gallery.querySelector<HTMLImageElement>(
      '[data-carousel-slide][data-thumbnail="true"]:not(.hidden) picture img',
    );
    return img ? img.currentSrc || img.src : undefined;
  };
  // Whenever the card is near the header showing another photo (it may have been swiped to again since), slide it
  // back to the bottle, so the bottle is what lifts out of it and comes back to it (carousel.ts listens for
  // carousel:goto; asking again mid-slide just carries on to the same slide).
  window.addEventListener(
    'scroll',
    () => {
      if (!narrow.matches) return;
      const box = gallery.getBoundingClientRect();
      if (box.top > viewHeight() * 0.25 || box.bottom < 0) return;
      const showing = visibleGalleryPhoto(gallery);
      const bottle =
        showing?.closest<HTMLElement>('[data-carousel-slide]')?.dataset
          .thumbnail === 'true';
      if (showing && !bottle)
        gallery.dispatchEvent(
          new CustomEvent('carousel:goto', { detail: { index: 0 } }),
        );
    },
    { passive: true },
  );
  flyViaHeader({
    source: () => visibleGalleryPhoto(gallery),
    photo: bottlePhoto,
    target: photo,
    reveal: slot,
    progress: () =>
      narrow.matches
        ? span(
            viewHeight() - track.getBoundingClientRect().top,
            0,
            viewHeight() - (header?.getBoundingClientRect().bottom ?? 0),
          )
        : 0,
    tuckWhen: narrow,
    onFlying: (value) => (handingOff = value),
  });
}

/**
 * The reverse of the hand-off: as the story lets go, its sealed bottle shrinks and flies down to land as the
 * small bottle beside the "Backed by real routines" heading, so the page reads as one bottle travelling on.
 * The heading's bottle ([data-ing-dock]) is only there on the product page; the visible one is the target
 * (the page has a mobile and a desktop copy of that section).
 */
function dockIntoRoutines(track: HTMLElement, slot: HTMLElement) {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const photo = slot.querySelector<HTMLImageElement>('img');
  const docks = Array.from(
    document.querySelectorAll<HTMLImageElement>('[data-ing-dock]'),
  );
  if (!photo || !docks.length) return;
  const stage = slot.closest<HTMLElement>('.ing-stage');
  const page = slot.closest<HTMLElement>('.ing');
  const { ghost, img: ghostImg } = createGhost();

  let dock: HTMLImageElement | undefined;
  let flying = false;
  let frame = 0;

  const land = () => {
    ghost.style.opacity = '0';
    slot.style.opacity = '';
    if (dock) dock.style.opacity = '';
    dock = undefined;
    flying = false;
  };

  const draw = () => {
    frame = 0;
    const trackBottom = track.getBoundingClientRect().bottom;
    // How far the stage has scrolled up since it let go: its pinned bottom edge minus the track's. (Not the window's
    // height: on phones that grows as the address bar hides, which read as the stage leaving while still pinned.)
    const top = page
      ? parseFloat(getComputedStyle(page).getPropertyValue('--ing-top')) || 0
      : 0;
    const pinnedBottom = stage ? top + stage.offsetHeight : viewHeight();
    const scrolledAway = pinnedBottom - trackBottom;
    dock ??= docks.find((d) => d.offsetParent !== null); // the copy that's displayed at this width
    const dockRect = dock?.getBoundingClientRect();
    if (!dock || !dockRect?.height || scrolledAway <= 0) {
      if (flying) land();
      return;
    }
    // Total travel: from the stage letting go until the heading's bottle reaches DOCK_AT.
    const travel = dockRect.top + scrolledAway - viewHeight() * DOCK_AT;
    const a = travel > 0 ? span(scrolledAway, 0, travel) : 1;
    if (a >= 1) {
      if (flying) land();
      return;
    }
    flying = true;

    const src = photo.currentSrc || photo.src;
    if (ghostImg.getAttribute('src') !== src) ghostImg.src = src;

    const from = boxOf(photo.getBoundingClientRect());
    const to = boxOf(dockRect);
    if (!from.h) return;
    const t = easeInOut(a);
    const arc = Math.sin(Math.PI * t);
    const x = lerp(from.x, to.x, t);
    const y = lerp(from.y, to.y, t) - arc * from.h * HANDOFF_LIFT;
    const s = lerp(1, to.h / from.h, t);

    const depart = span(a, ...DOCK_DEPART);
    const landed = span(a, ...DOCK_LAND);
    ghost.style.width = `${from.w.toFixed(1)}px`;
    ghost.style.height = `${from.h.toFixed(1)}px`;
    ghost.style.transform = `translate(${(x - from.w / 2).toFixed(1)}px,${(y - from.h / 2).toFixed(1)}px) rotate(${(arc * HANDOFF_TILT).toFixed(2)}deg) scale(${s.toFixed(4)})`;
    ghost.style.opacity = Math.min(depart, 1 - landed).toFixed(2);
    slot.style.opacity = (1 - depart).toFixed(2);
    dock.style.opacity = landed.toFixed(2);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(draw);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      if (dock?.offsetParent === null) dock = undefined; // the mobile/desktop copy swapped (not just the address bar)
      schedule();
    },
    { passive: true },
  );
  if (window.scrollY > 0) schedule(); // a visit from the top has nothing to draw yet (see scrollScenes)
}

/**
 * Lifts the product photo out of the buy-box gallery and flies it down into the stage as the story
 * scrolls in, turning into the 3D bottle on the way, so the story reads as the same bottle opening up
 * rather than a new section. A fixed "ghost" flies the flat photo; the real bottle slot flies the rest
 * (the stage's overflow is opened up for it until it lands).
 */
function handOffFromGallery(
  page: HTMLElement,
  track: HTMLElement,
  slot: HTMLElement,
) {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Not in the narrow layout (same breakpoint as the CSS): there the gallery is far up the page, so galleryIntoStory
  // takes over. Checked every frame, so it follows a resize either way.
  const narrow = matchMedia('(max-width: 900px)');
  const gallery = document.querySelector<HTMLElement>('[data-ing-handoff]');
  const photo = slot.querySelector<HTMLImageElement>('img');
  const stage = slot.closest<HTMLElement>('.ing-stage');
  if (!gallery || !photo || !stage) return;

  const { ghost, img: ghostImg } = createGhost();

  const galleryPhoto = () => visibleGalleryPhoto(gallery);

  let source: HTMLImageElement | undefined;
  let flying = false;
  let frame = 0;
  // The flight offset last written to the slot, needed to work out where its resting place is.
  let fly = { x: 0, y: 0, s: 1 };

  const setFly = (x: number, y: number, s: number) => {
    fly = { x, y, s };
    slot.style.setProperty('--ing-fly-x', `${x.toFixed(1)}px`);
    slot.style.setProperty('--ing-fly-y', `${y.toFixed(1)}px`);
    slot.style.setProperty('--ing-fly-s', s.toFixed(4));
  };

  const land = () => {
    ghost.style.opacity = '0';
    slot.style.opacity = '';
    ['--ing-fly-x', '--ing-fly-y', '--ing-fly-s'].forEach((p) =>
      slot.style.removeProperty(p),
    );
    fly = { x: 0, y: 0, s: 1 };
    stage.style.overflow = '';
    if (source) source.style.opacity = '';
    source = undefined;
    bottle?.setRotation(0, 0); // front-on, where the story's own rotation starts
    flying = handingOff = false;
  };

  const draw = () => {
    frame = 0;
    const top =
      parseFloat(getComputedStyle(page).getPropertyValue('--ing-top')) || 0;
    const a = span(
      viewHeight() - track.getBoundingClientRect().top,
      0,
      viewHeight() - top,
    );
    if (narrow.matches || a <= 0 || a >= 1) {
      if (flying) land();
      return;
    }
    source ??= galleryPhoto();
    if (!source) return;
    if (!flying) stage.style.overflow = 'visible'; // let the bottle fly in from above the stage
    flying = handingOff = true;

    const src = photo.currentSrc || photo.src;
    if (ghostImg.getAttribute('src') !== src) ghostImg.src = src;

    // Where the bottle rests (its photo box with no flight applied): undo the last offset, which moved
    // the slot's centre by (x, y) and scaled everything in it about that centre by s.
    const slotNow = boxOf(slot.getBoundingClientRect());
    const photoNow = boxOf(photo.getBoundingClientRect());
    if (!photoNow.h) return;
    const centre = { x: slotNow.x - fly.x, y: slotNow.y - fly.y };
    const home: Box = {
      x: centre.x + (photoNow.x - slotNow.x) / fly.s,
      y: centre.y + (photoNow.y - slotNow.y) / fly.s,
      w: photoNow.w / fly.s,
      h: photoNow.h / fly.s,
    };
    const from = boxOf(source.getBoundingClientRect());
    // Where the photo has to be for its tub to cover the 3D tub.
    const aim =
      bottle && bottleKind === 'bottle'
        ? tubLanding(bottle, home, centre)
        : home;

    // The path: gallery -> beside the headline at HANDOFF_LAND_SCALE, then grow in place to full size.
    const t = easeInOut(span(a, 0, HANDOFF_GROW[0]));
    const arc = Math.sin(Math.PI * t);
    const grow = easeInOut(span(a, ...HANDOFF_GROW));
    const x = lerp(from.x, aim.x, t);
    const y = lerp(from.y, aim.y, t) - arc * aim.h * HANDOFF_LIFT;
    const h = lerp(
      lerp(from.h, aim.h * HANDOFF_LAND_SCALE, t) * (1 + arc * 0.06),
      aim.h,
      grow,
    );
    const s = h / aim.h;

    // Flat photo: departs, tilts and straightens again, then dissolves into the (front-on) 3D bottle on the same path.
    const depart = span(a, ...HANDOFF_DEPART);
    const morph = easeInOut(span(a, ...HANDOFF_MORPH));
    const tilt =
      Math.sin(Math.PI * span(a, 0, HANDOFF_MORPH[0])) * HANDOFF_TILT;
    ghost.style.width = `${aim.w.toFixed(1)}px`;
    ghost.style.height = `${aim.h.toFixed(1)}px`;
    ghost.style.transform = `translate(${(x - aim.w / 2).toFixed(1)}px,${(y - aim.h / 2).toFixed(1)}px) rotate(${tilt.toFixed(2)}deg) scale(${s.toFixed(4)})`;
    ghost.style.opacity = Math.min(depart, 1 - morph).toFixed(2);
    source.style.opacity = (1 - depart).toFixed(2);

    // 3D bottle: scale the slot about its centre so the landing box sits on the ghost.
    setFly(
      x - centre.x - s * (aim.x - centre.x),
      y - centre.y - s * (aim.y - centre.y),
      s,
    );
    slot.style.opacity = morph.toFixed(2);
    // One slow turn once it's fully 3D (front-on while it crossfades with the photo), back to front-on as it lands.
    bottle?.setRotation(
      TAU * easeInOut(span(a, HANDOFF_MORPH[1], HANDOFF_GROW[0])),
      0,
    );
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(draw);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  if (window.scrollY > 0) schedule(); // a visit from the top has nothing to draw yet (see scrollScenes)
}

/**
 * The photo box (same shape as the stage photo's rest box, home) whose tub lands exactly on the 3D tub's outline,
 * from the render's projected base and cap top. The 3D canvas fills the slot, whose rest centre is centre.
 */
function tubLanding(model: Bottle3D, home: Box, centre: Point): Box {
  const canvas = document.querySelector<HTMLCanvasElement>(
    '[data-ing-bottle] canvas',
  );
  if (!canvas?.clientHeight) return home;
  const base = model.projectAxis(0);
  const top = model.projectAxis(model.height);
  if (base.y <= top.y) return home;
  const h = (base.y - top.y) / PACKSHOT_TUB.fill;
  return {
    x: centre.x + (base.x + top.x) / 2 - canvas.clientWidth / 2,
    y:
      centre.y +
      (base.y + top.y) / 2 -
      canvas.clientHeight / 2 -
      (PACKSHOT_TUB.centre - 0.5) * h,
    w: h * (home.w / home.h),
    h,
  };
}

type Point = { x: number; y: number };

const quadratic = (a: Point, c: Point, b: Point, t: number): Point => ({
  x: (1 - t) * (1 - t) * a.x + 2 * (1 - t) * t * c.x + t * t * b.x,
  y: (1 - t) * (1 - t) * a.y + 2 * (1 - t) * t * c.y + t * t * b.y,
});

/** Set by IngredientsStoryVm.Layout from the ingredient count. */
type Layout = 'orbit' | 'swarm' | 'petals';
/** Petals: the chips meet above the neck and merge into one split chip, which then pours in. */
const MERGE = [0.6, 0.72] as const;
/** Swarm: scroll each copy takes to drop in (there are dozens, so far less than POUR_EACH). */
const SWARM_EACH = 0.03;

/** Repeatable 0..1 noise, so the swarm looks the same on every visit. */
const noise = (i: number, k: number) => {
  const v = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
  return v - Math.floor(v);
};

/** Clips each image of the merged chip to its own slice (halves, thirds, quarters). */
function sliceMerged(merged: HTMLElement) {
  const images = Array.from(merged.querySelectorAll<HTMLImageElement>('img'));
  images.forEach((img, i) => {
    const points = ['50% 50%'];
    for (let k = 0; k <= 12; k++) {
      const a = -Math.PI / 2 + ((i + k / 12) / images.length) * TAU;
      points.push(
        `${(50 + Math.cos(a) * 75).toFixed(1)}% ${(50 + Math.sin(a) * 75).toFixed(1)}%`,
      );
    }
    img.style.clipPath = `polygon(${points.join(',')})`;
  });
}

function initStory(stage: HTMLElement): SceneRender {
  const field = stage.querySelector<HTMLElement>('[data-ing-field]')!;
  const bottleEl = stage.querySelector<HTMLElement>('[data-ing-bottle]')!;
  const layout = (field.dataset.ingLayout ?? 'orbit') as Layout;
  const packSize = Number(field.dataset.ingPack) || 0;
  const chips = Array.from(
    field.querySelectorAll<HTMLElement>(
      '.ing-chip:not([data-ing-merged]), .ing-mote',
    ),
  );
  const merged = field.querySelector<HTMLElement>('[data-ing-merged]');
  if (merged) sliceMerged(merged);
  const steps = stage.querySelectorAll<HTMLElement>('.ing-step');
  const count = stage.querySelector<HTMLElement>('[data-ing-count]');
  const hint = stage.querySelector<HTMLElement>('[data-ing-hint]');
  const detail = stage.querySelector<HTMLElement>('[data-ing-detail]')!;
  const detailItems = Array.from(
    detail.querySelectorAll<HTMLElement>('[data-ing-detail-item]'),
  );
  const summary = stage.querySelector<HTMLElement>('[data-ing-summary]');
  const glow = stage.querySelector<HTMLElement>('.ing-glow');

  // Last values written, so unchanged ones aren't touched again (every write costs style/paint work).
  const chipLayer: string[] = [];
  let lastStep = -1;
  let lastPoured = -1;
  let lastHint = '';
  let lastCap = [NaN, NaN];
  let lastRot = NaN;
  let posedBottle: Bottle3D | undefined;

  const n = chips.length;
  // Front-centre slot sits between two chips at rest so the label stays clear.
  const START = Math.PI / 2 - Math.PI / n;
  // The swarm is one ingredient, so its card shows from the start and never changes.
  let front = layout === 'swarm' ? 0 : -1;
  const motes = chips.map((_, i) => ({
    a: noise(i, 1) * TAU,
    rad: 0.55 + noise(i, 2) * 0.55,
    wobble: noise(i, 3) - 0.5,
    speed: 0.7 + noise(i, 4) * 0.8,
    size: 0.75 + noise(i, 5) * 0.5,
  }));
  // Chips appear one after another; keep the whole intro inside INTRO however many there are.
  const stagger = Math.min(0.004, 0.07 / n);
  const orbitEnd = layout === 'petals' ? MERGE[0] : ORBIT[1] - 0.02;
  let lastMerged = '';

  /** Neck opening relative to the field origin (the bottle's centre). */
  const mouth = (): Point => {
    if (bottle) {
      const m = bottle.projectAxis(bottle.mouthY);
      const b = bottleEl.getBoundingClientRect();
      const f = field.getBoundingClientRect();
      return { x: b.left + m.x - f.left, y: b.top + m.y - f.top };
    }
    return { x: 0, y: -bottleEl.offsetHeight * 0.32 };
  };

  /** Where item i sits at orbit progress t; depth 0 = behind the bottle, 1 = in front. */
  const position = (i: number, t: number, rx: number, ry: number) => {
    if (layout === 'swarm') {
      // A loose cloud: each copy on its own radius and speed, bobbing up and down.
      const m = motes[i];
      const a = m.a + t * TAU * 1.4 * m.speed;
      const y =
        Math.sin(a) * ry * m.rad +
        Math.cos(a * 2) * stage.clientHeight * 0.06 * m.wobble;
      return { x: Math.cos(a) * rx * m.rad, y, depth: (Math.sin(a) + 1) / 2 };
    }
    if (layout === 'petals' && n === 2) {
      // Figure-eight: wide left-right loops crossing in front of the bottle.
      const u = t * TAU * 1.5 + i * Math.PI;
      return {
        x: Math.sin(u) * rx * 1.05,
        y: Math.sin(u) * Math.cos(u) * ry * 2.2,
        depth: (Math.cos(u) + 1) / 2,
      };
    }
    if (layout === 'petals') {
      // One petal per ingredient (|cos(n/2 θ)| has n lobes); staggered so they take turns at the tips.
      const th = t * TAU * 0.9 + (i / n) * TAU + (i / n) * (TAU / n) * 0.5;
      const r = 0.38 + 0.62 * Math.abs(Math.cos((n / 2) * th));
      return {
        x: Math.cos(th) * r * rx * 1.1,
        y: Math.sin(th) * r * ry * 1.9,
        depth: (Math.sin(th) + 1) / 2,
      };
    }
    const angle = (i / n) * TAU + t * TAU * 1.2 + START;
    return {
      x: Math.cos(angle) * rx,
      y: Math.sin(angle) * ry,
      depth: (Math.sin(angle) + 1) / 2,
    };
  };

  /** Each item's own drop into the neck; petals pour as one merged chip instead. */
  const pourOf = (i: number, p: number) => {
    if (layout === 'petals') return 0;
    const each = layout === 'swarm' ? SWARM_EACH : POUR_EACH;
    const start = POUR[0] + (i / n) * (POUR[1] - POUR[0] - each);
    return easeInOut(span(p, start, start + each));
  };

  const showDetail = (index: number) =>
    detailItems.forEach((item, i) => (item.hidden = i !== index));

  // Picking a chip shows its card and holds it, pausing the idle auto-scroll, until the story moves on. In the orbit
  // the page also scrolls to where that chip comes round to the front-centre.
  let picked = -1;
  let pickedAt = 0; // story progress the pick is held at
  let pickedArrived = false;
  let pickedUntil = 0; // gives up waiting to arrive after this (performance.now())
  let lastP = 0;
  const track = stage.closest<HTMLElement>('.ing-track');
  const page = stage.closest<HTMLElement>('.ing');

  /** Story progress at which chip i is front-centre of the orbit, nearest the current one (NaN if none). */
  const progressToFront = (i: number) => {
    // position(): angle = (i / n) TAU + t TAU 1.2 + START, and the front is angle = TAU / 4 (mod TAU).
    const turn = 1 / 1.2; // orbit progress for one full turn
    const first = ((TAU / 4 - START) / TAU - i / n) * turn;
    const now = easeInOut(span(lastP, ...ORBIT));
    const last = easeInOut(span(orbitEnd - 0.01, ...ORBIT));
    let best = NaN;
    for (let k = -3; k <= 3; k++) {
      const t = first + k * turn;
      if (t < 0 || t > last) continue;
      if (Number.isNaN(best) || Math.abs(t - now) < Math.abs(best - now))
        best = t;
    }
    if (Number.isNaN(best)) return NaN;
    // Undo easeInOut, then map orbit progress back onto the story's.
    const x =
      best < 0.5 ? Math.sqrt(best / 2) : 1 - Math.sqrt(2 * (1 - best)) / 2;
    return lerp(ORBIT[0], ORBIT[1], x);
  };

  const scrollToProgress = (p: number) => {
    if (!track || !page) return;
    const top =
      parseFloat(getComputedStyle(page).getPropertyValue('--ing-top')) || 0;
    const travel = track.offsetHeight - stage.offsetHeight;
    const instant = matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top:
        track.getBoundingClientRect().top + window.scrollY - top + p * travel,
      behavior: instant ? 'auto' : 'smooth',
    });
  };

  chips.forEach((chip, i) =>
    chip.querySelector('[data-ing-pick]')?.addEventListener('click', () => {
      picked = front = i;
      showDetail(i);
      chips.forEach((c, j) => c.classList.toggle('is-front', j === i));
      idleHeldUntil = performance.now() + PICK_HOLD_MS;
      const to = layout === 'orbit' ? progressToFront(i) : NaN;
      if (Number.isNaN(to)) {
        pickedAt = lastP;
        pickedArrived = true;
        return;
      }
      pickedAt = to;
      pickedArrived = false;
      pickedUntil = performance.now() + 2500;
      scrollToProgress(to);
    }),
  );

  const draw = (p: number) => {
    lastP = p;
    if (bottle !== posedBottle) {
      // A newly loaded bottle needs its pose written even if nothing else moved.
      posedBottle = bottle;
      lastCap = [NaN, NaN];
      lastRot = NaN;
    }
    const width = stage.clientWidth;
    const narrow = width < 900;
    const rx = narrow ? width * 0.4 : Math.min(width * 0.3, 520);
    const ry = narrow ? stage.clientHeight * 0.12 : rx * 0.3;
    const orbitT = easeInOut(span(p, ...ORBIT));
    const target = mouth();
    let poured = 0;
    let best = -1;
    let bestDepth = -1;

    const height = stage.clientHeight;
    // Petals: where the chips meet, above the neck, and how far they've come together.
    const meet = { x: target.x, y: target.y - height * 0.16 };
    const merge = layout === 'petals' ? easeInOut(span(p, ...MERGE)) : 0;
    const handOver =
      layout === 'petals'
        ? easeInOut(span(p, MERGE[1] - 0.02, MERGE[1] + 0.02))
        : 0;
    const arcInto = (from: Point, t: number) =>
      quadratic(
        from,
        { x: lerp(from.x, target.x, 0.6), y: target.y - height * 0.3 },
        target,
        t,
      );

    chips.forEach((chip, i) => {
      const appear = easeInOut(
        span(p, INTRO[0] + i * stagger, INTRO[1] + i * stagger),
      );
      const pour = pourOf(i, p);
      const at = position(i, orbitT, rx, ry);
      const scaleOrbit =
        (0.72 + at.depth * 0.4) * (layout === 'swarm' ? motes[i].size : 1);

      let { x, y } = at;
      let scale = scaleOrbit * appear;
      let opacity = appear * (0.55 + at.depth * 0.45) * (1 - handOver);

      if (merge > 0) {
        // Gather round the meeting point, then hand over to the merged chip.
        const a = (i / n) * TAU;
        x = lerp(x, meet.x + Math.cos(a) * 46 * (1 - merge), merge);
        y = lerp(y, meet.y + Math.sin(a) * 30 * (1 - merge), merge);
      }
      if (pour > 0) {
        // Arc from the orbit slot up over the bottle and down into the neck.
        ({ x, y } = arcInto(at, pour));
        scale = lerp(scaleOrbit, 0.12, pour);
        opacity = pour > 0.85 ? (1 - pour) / 0.15 : 1;
        if (pour >= 1) poured++;
      } else if (at.depth > bestDepth && appear > 0.5 && layout !== 'swarm') {
        bestDepth = at.depth;
        best = i;
      }

      chip.style.transform = `translate(-50%,-50%) translate(${x.toFixed(1)}px,${y.toFixed(1)}px) scale(${scale.toFixed(3)})`;
      // Depth cue by fading the far side (opacity is cheap to animate; a changing blur re-rasterises every frame).
      chip.style.opacity = opacity.toFixed(2);
      // In front of the bottle on the near half of the orbit and while dropping in from above.
      const layer = pour > 0 || merge > 0 || at.depth > 0.5 ? '3' : '1';
      if (chipLayer[i] !== layer) chip.style.zIndex = chipLayer[i] = layer;
    });

    if (merged) {
      const pour = easeInOut(span(p, POUR[0], POUR[1] - 0.02));
      const { x, y } = pour > 0 ? arcInto(meet, pour) : meet;
      const scale = lerp(1, 0.15, pour) * (0.85 + handOver * 0.15);
      const opacity = handOver * (pour > 0.85 ? (1 - pour) / 0.15 : 1);
      const transform = `translate(-50%,-50%) translate(${x.toFixed(1)}px,${y.toFixed(1)}px) scale(${scale.toFixed(3)})`;
      if (transform !== lastMerged) {
        merged.style.transform = lastMerged = transform;
        merged.style.opacity = opacity.toFixed(2);
      }
      if (pour >= 1) poured = n;
    }
    // The swarm counts tablets, not copies.
    if (layout === 'swarm' && packSize)
      poured = Math.round((poured / n) * packSize);

    const orbiting = p < orbitEnd;
    if (picked >= 0) {
      if (!pickedArrived && Math.abs(p - pickedAt) < 0.005)
        pickedArrived = true;
      // Let go once the story moves on from the pick, or if it never got there (the visitor scrolled away).
      const away = pickedArrived
        ? Math.abs(p - pickedAt) > PICK_RELEASE
        : performance.now() > pickedUntil;
      if (!orbiting || away) picked = -1;
    }
    if (orbiting && picked < 0 && best !== front && best >= 0) {
      front = best;
      showDetail(best);
    }
    if (layout !== 'swarm')
      chips.forEach((chip, i) =>
        chip.classList.toggle('is-front', orbiting && i === front),
      );
    detail.classList.toggle('is-hidden', !orbiting || p < INTRO[1]);

    // Opens for the pour (lid off / top torn), then closes again (lid screwed down / zipper pressed shut).
    const seal = easeInOut(span(p, ...SEAL));
    const open = easeInOut(span(p, ...OPEN));
    // Only redraw the 3D model when its pose actually changed.
    if (
      Math.abs(open - lastCap[0]) > 1e-4 ||
      Math.abs(seal - lastCap[1]) > 1e-4 ||
      !(lastCap[0] === lastCap[0])
    ) {
      bottle?.setOpening(open, seal);
      lastCap = [open, seal];
    }
    // Front-on at the very start, matching the flat gallery photo it's handed off from, then into the sweep.
    const rot =
      lerp(-0.3, 0.3, orbitT) *
      (1 - seal) *
      easeInOut(span(p, 0, INTRO[1] + 0.03));
    if (!(Math.abs(rot - lastRot) < 1e-4)) {
      bottle?.setRotation(rot, 0);
      lastRot = rot;
    }

    // Glow swells as the bottle fills, then settles once sealed.
    const fill = span(p, ...POUR);
    // Set on the glow itself: a custom property on the stage would restyle everything inside it.
    if (glow) {
      glow.style.opacity = (0.45 + fill * 0.45 - seal * 0.2).toFixed(2);
      glow.style.transform = `translate(-50%, -50%) scale(${(1 + fill * 0.15).toFixed(3)})`;
    }

    if (count && poured !== lastPoured)
      count.textContent = String((lastPoured = poured));
    const step = p < 0.1 ? 0 : p < POUR[0] ? 1 : p < SEAL[0] ? 2 : 3;
    if (step !== lastStep) {
      lastStep = step;
      steps.forEach((s, i) => s.classList.toggle('is-on', i === step));
      summary?.classList.toggle('is-hidden', step !== 3);
    }
    const hintOpacity = p > 0.02 ? '0' : '1';
    if (hint && hintOpacity !== lastHint)
      hint.style.opacity = lastHint = hintOpacity;
  };

  // Finale autoplay: once the lid has started lifting, the pour and seal play out on their own
  // instead of needing more scroll. Scrolling back above the trigger rewinds it smoothly.
  let finale = 0; // 0..1 through the autoplayed finale
  let held = 0; // seconds spent at the trigger before it plays
  return (p, dt) => {
    if (p >= FINALE_TRIGGER) {
      held += dt;
      if (held >= FINALE_HOLD_SECONDS)
        finale = Math.min(1, finale + dt / FINALE_SECONDS);
    } else {
      held = 0;
      finale = Math.max(0, finale - dt / FINALE_REWIND_SECONDS);
    }
    draw(finale > 0 ? Math.max(p, lerp(FINALE_TRIGGER, 1, finale)) : p);
    return p >= FINALE_TRIGGER ? finale < 1 : finale > 0; // keep ticking while it holds or plays
  };
}

onReady(() => {
  const page = document.querySelector<HTMLElement>('.ing');
  if (!page) return;

  const redraw = mountScrollScenes(
    page,
    { story: initStory },
    { sceneKey: 'ingScene', stageSelector: '.ing-stage', topVar: '--ing-top' },
  );

  const track = page.querySelector<HTMLElement>('.ing-track');
  const stage = page.querySelector<HTMLElement>('.ing-stage');
  if (track && stage) autoAdvanceWhenIdle(track, stage);

  const slot = page.querySelector<HTMLElement>('[data-ing-bottle]');
  if (!slot) return;
  if (track) {
    handOffFromGallery(page, track, slot);
    galleryIntoStory(track, slot);
    dockIntoRoutines(track, slot);
    dockIntoWhy();
  }
  const { label: labelUrl, lid, pouchFront, pouchBack } = slot.dataset;
  // Variants sold in a pouch (multivitamin 100s): picking one swaps the tub for the pouch.
  const pouchVariants = new Set(
    (slot.dataset.pouchVariants ?? '').split(',').filter(Boolean),
  );
  const canPouch = Boolean(pouchFront && pouchBack && pouchVariants.size);
  const wantsPouch = (varId: string | undefined) =>
    canPouch && pouchVariants.has(varId ?? '');

  let kind: 'bottle' | 'pouch' = wantsPouch(
    document.querySelector<HTMLInputElement>('#input-varId')?.value,
  )
    ? 'pouch'
    : 'bottle';
  let ready = false; // 3D is allowed to load (visitor interacting, story close)
  let loading = 0; // latest load wins if the variant changes mid-load

  const load = async () => {
    const ticket = ++loading;
    const want = kind;
    if (want === 'bottle' && !labelUrl) return;
    try {
      const created =
        want === 'pouch'
          ? await import('./components/pouch3d').then(({ createPouch3D }) =>
              createPouch3D(slot, {
                frontUrl: pouchFront!,
                backUrl: pouchBack!,
                headroom: HEADROOM,
              }),
            )
          : await import('./components/bottle3d').then(({ createBottle3D }) =>
              createBottle3D(slot, {
                labelUrl: labelUrl!,
                lid: lid === 'silver' ? 'silver' : 'black',
                headroom: HEADROOM,
              }),
            );
      if (ticket !== loading) {
        created.dispose(); // superseded by a newer variant pick
        return;
      }
      bottle?.dispose();
      bottle = created;
      bottleKind = want;
      slot.classList.add('is-3d');
      redraw();
      // Once the canvas has faded in, wiggle and give it a little opening play so it reads as interactive -
      // unless the gallery photo is mid-flight into it, where a wiggle would read as the landing jumping.
      setTimeout(() => !handingOff && created.nudge({ lid: true }), 450);
    } catch {
      /* no WebGL, or no artwork for this product yet: keep the product photo */
    }
  };

  // Announced by the purchase box when a different variant is picked (productpurchase.ts).
  document.addEventListener('product:variant', (e) => {
    const { varId, suffix } = (
      e as CustomEvent<{ varId: string; suffix: string }>
    ).detail;
    if (suffix) return; // only the page's main purchase box, not upsell copies
    const next = wantsPouch(varId) ? 'pouch' : 'bottle';
    if (next === kind) return;
    kind = next;
    if (ready) void load();
  });

  // 3D waits until the visitor is interacting and the story is close; the photo stands in until then.
  void Promise.all([firstInteraction(), nearViewport(page, '600px')]).then(
    () => {
      ready = true;
      return load();
    },
  );
});
